import { MessageContext } from 'contexts/MessageContext';
import React, { memo, useContext } from 'react';
import { useRecoilValue } from 'recoil';

import {
    type IAction,
    type IMessageElement,
    type IStep
} from '@chainlit/react-client';

import BlinkingCursor from '@/components/BlinkingCursor';

import { debugModeState } from 'state/chat';

import { Message } from './Message';

const RUNS_WITHOUT_LOADER = ['on_chat_start', 'on_audio_end'];

interface Props {
    messages: IStep[];
    elements: IMessageElement[];
    actions: IAction[];
    indent: number;
    isRunning?: boolean;
    scorableRun?: IStep;
}

const CL_RUN_NAMES = ['on_chat_start', 'on_message', 'on_audio_end'];

const hasActiveToolStep = (step: IStep): boolean => {
    return (
        step.steps?.some(
            (s) =>
                (s.type === 'tool' && s.start && !s.end && !s.isError) ||
                s.type.includes('message') ||
                hasActiveToolStep(s)
        ) || false
    );
};

const hasAssistantMessage = (step: IStep): boolean => {
    return (
        step.steps?.some(
            (s) => s.type === 'assistant_message' || hasAssistantMessage(s)
        ) || false
    );
};

const Messages = memo(
    ({
        messages,
        elements,
        actions,
        indent,
        isRunning,
        scorableRun
    }: Props) => {
        const messageContext = useContext(MessageContext);
        const debugMode = useRecoilValue(debugModeState);

        const messagesFiltered = messages.filter((m) => {
            // If debug mode is enabled, show all messages including tools
            if (debugMode) {
                return true;
            }
            // Always show steps with alwaysShow metadata (e.g. file upload, processing progress)
            if (m.type === 'tool' && m.metadata?.alwaysShow) {
                return true;
            }
            // Otherwise, filter out tool messages
            return m.type !== 'tool';
        });

        return (
            <>
                {messagesFiltered.map((m) => {
                    // Handle chainlit runs
                    if (CL_RUN_NAMES.includes(m.name)) {
                        const isRunning =
                            !m.end && !m.isError && messageContext.loading;
                        const isToolCallCoT =
                            messageContext.cot === 'tool_call' ||
                            messageContext.cot === 'full';
                        const isHiddenCoT = messageContext.cot === 'hidden';

                        const showToolCoTLoader = isToolCallCoT
                            ? isRunning && !hasActiveToolStep(m)
                            : false;

                        const showHiddenCoTLoader = isHiddenCoT
                            ? isRunning && !hasAssistantMessage(m)
                            : false;
                        // Ignore on_chat_start for scorable run
                        const scorableRun =
                            !isRunning && m.name !== 'on_chat_start'
                                ? m
                                : undefined;
                        return (
                            <React.Fragment key={m.id}>
                                {m.steps?.length ? (
                                    <Messages
                                        messages={m.steps}
                                        elements={elements}
                                        actions={actions}
                                        indent={indent}
                                        isRunning={isRunning}
                                        scorableRun={scorableRun}
                                    />
                                ) : null}
                                {(showToolCoTLoader || showHiddenCoTLoader) &&
                                !RUNS_WITHOUT_LOADER.includes(m.name) ? (
                                    <BlinkingCursor />
                                ) : null}
                            </React.Fragment>
                        );
                    } else {
                        // Score the current run
                        const _scorableRun = m.type === 'run' ? m : scorableRun;
                        // The message is scorable if it is the last assistant message of the run

                        const isRunLastAssistantMessage =
                            m ===
                            _scorableRun?.steps?.findLast(
                                (_m) => _m.type === 'assistant_message'
                            );

                        const isLastAssistantMessage =
                            messages.findLast(
                                (_m) => _m.type === 'assistant_message'
                            ) === m;

                        const isScorable =
                            isRunLastAssistantMessage || isLastAssistantMessage;

                        return (
                            <Message
                                message={m}
                                elements={elements}
                                actions={actions}
                                key={m.id}
                                indent={indent}
                                isRunning={isRunning}
                                scorableRun={_scorableRun}
                                isScorable={isScorable}
                                isLastAssistantInRun={
                                    isRunLastAssistantMessage ||
                                    isLastAssistantMessage
                                }
                            />
                        );
                    }
                })}
            </>
        );
    }
);

export { Messages };
